import { useState } from 'react';

export default function LeadForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    website: '',
    messenger: '',
    service: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const form = e.currentTarget as HTMLFormElement;
      const formDataToSend = new FormData(form);
      await fetch('https://readdy.ai/api/form/d4hkhqser93evkarrqo0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataToSend as any).toString()
      });
      setSubmitStatus('success');
      setFormData({ name: '', phone: '', website: '', messenger: '', service: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const inputClass = "w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-[#C5A880] text-white placeholder-white/40 text-sm font-['Montserrat'] transition-colors duration-200";
  const selectClass = "w-full px-4 py-3 bg-[#1A2B4C] border border-white/20 rounded-lg focus:outline-none focus:border-[#C5A880] text-white text-sm font-['Montserrat'] transition-colors duration-200 cursor-pointer";

  return (
    <section id="lead-form" className="py-24 bg-[#1A2B4C] relative overflow-hidden">
      {/* Декоративные элементы */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full border border-[#C5A880]/10 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full border border-[#C5A880]/10 translate-x-1/3 translate-y-1/3 pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 w-64 h-64 rounded-full border border-[#C5A880]/5 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Заголовок */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#C5A880]/40 rounded-full mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-[#C5A880]" />
            <span className="text-[#C5A880] text-xs font-semibold tracking-widest uppercase font-['Montserrat']">Бесплатная диагностика</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 font-['Playfair_Display']">
            Готовы превратить хаос<br />в стабильный рост?
          </h2>
          <p className="text-white/60 text-lg font-['Montserrat'] max-w-xl mx-auto">
            Оставьте заявку — мы покажем точки роста с помощью ИИ-анализа
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Левая колонка — преимущества */}
          <div className="space-y-8">
            {[
              {
                icon: 'ri-search-eye-line',
                title: 'Глубокий анализ',
                desc: 'Изучим ваш бизнес и конкурентов с помощью AI-инструментов и выявим скрытые точки роста'
              },
              {
                icon: 'ri-presentation-line',
                title: 'Персональная стратегия',
                desc: 'Покажем конкретные точки роста и разработаем индивидуальную маркетинговую стратегию'
              },
              {
                icon: 'ri-gift-line',
                title: 'Бонус в подарок',
                desc: 'Чек-лист быстрых улучшений, которые можно внедрить сразу после диагностики'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Без обязательств',
                desc: 'Диагностика абсолютно бесплатна. Вы сами решаете, продолжать сотрудничество или нет'
              }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-5 group">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#C5A880]/10 border border-[#C5A880]/30 flex-shrink-0 group-hover:bg-[#C5A880]/20 transition-colors duration-300">
                  <i className={`${item.icon} text-2xl text-[#C5A880]`} />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-1 font-['Playfair_Display']">{item.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed font-['Montserrat']">{item.desc}</p>
                </div>
              </div>
            ))}

            {/* Цитата */}
            <div className="mt-8 p-6 border-l-2 border-[#C5A880] bg-white/5 rounded-r-xl">
              <p className="text-white/70 text-sm italic leading-relaxed font-['Montserrat']">
                «Каждый клиент получает не шаблонное решение, а персональную систему, выстроенную под его бизнес и цели»
              </p>
              <p className="text-[#C5A880] text-sm font-semibold mt-3 font-['Montserrat']">— Ирина Шарова, основатель</p>
            </div>
          </div>

          {/* Правая колонка — форма */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-xl bg-[#C5A880]/15 border border-[#C5A880]/30">
                <i className="ri-calendar-check-line text-3xl text-[#C5A880]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2 font-['Playfair_Display']">
                Записаться на диагностику
              </h3>
              <p className="text-white/50 text-sm font-['Montserrat']">Свяжемся с вами в течение часа</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form id="lead-form-main">
              <div>
                <label className="block text-xs font-semibold text-[#C5A880] mb-2 tracking-wider uppercase font-['Montserrat']">
                  Ваше имя <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={inputClass}
                  placeholder="Иван Иванов"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#C5A880] mb-2 tracking-wider uppercase font-['Montserrat']">
                  Телефон <span className="text-red-400">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className={inputClass}
                  placeholder="+7 (999) 123-45-67"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#C5A880] mb-2 tracking-wider uppercase font-['Montserrat']">
                  Интересующая услуга <span className="text-red-400">*</span>
                </label>
                <select
                  name="service"
                  required
                  value={formData.service}
                  onChange={(e) => setFormData({...formData, service: e.target.value})}
                  className={selectClass}
                >
                  <option value="">Выберите услугу</option>
                  <option value="Цифровой Маркетинг 360°">Цифровой Маркетинг 360°</option>
                  <option value="Комплексная стратегия маркетинга">Комплексная стратегия маркетинга</option>
                  <option value="Консалтинг по маркетингу">Консалтинг по маркетингу</option>
                  <option value="Стратегическое управление и Партнерство">Стратегическое управление и Партнерство</option>
                  <option value="Консалтинг маркетплейс">Консалтинг маркетплейс</option>
                  <option value="Создание продающих сайтов">Создание продающих сайтов</option>
                  <option value="Контекстная реклама">Контекстная реклама</option>
                  <option value="Таргетированная реклама">Таргетированная реклама</option>
                  <option value="SMM и Контент">SMM и Контент</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#C5A880] mb-2 tracking-wider uppercase font-['Montserrat']">
                  Сайт <span className="text-white/30 normal-case font-normal">(необязательно)</span>
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  className={inputClass}
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#C5A880] mb-2 tracking-wider uppercase font-['Montserrat']">
                  Предпочитаемый мессенджер
                </label>
                <select
                  name="messenger"
                  value={formData.messenger}
                  onChange={(e) => setFormData({...formData, messenger: e.target.value})}
                  className={selectClass}
                >
                  <option value="">Выберите мессенджер</option>
                  <option value="telegram">Telegram</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-[#C5A880] hover:bg-[#b8976e] text-[#1A2B4C] font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-[#C5A880]/30 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer font-['Montserrat'] tracking-wide text-sm uppercase mt-2"
              >
                {isSubmitting ? 'Отправка...' : 'Записаться на диагностику'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-900/30 border border-green-500/30 text-green-300 px-4 py-3 rounded-lg text-sm font-['Montserrat']">
                  ✓ Заявка отправлена! Мы свяжемся с вами в ближайшее время.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-900/30 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg text-sm font-['Montserrat']">
                  Произошла ошибка. Попробуйте ещё раз.
                </div>
              )}

              <p className="text-xs text-white/30 text-center font-['Montserrat']">
                Нажимая на кнопку, я даю согласие на обработку персональных данных и соглашаюсь с политикой конфиденциальности
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
